<template>
    <div class="suggested-appointments rounded border-solid border-2 py-4 px-4 mt-3 border-grey-200">
        <!-- Top section: date range display + navigation buttons -->
        <div class="flex justify-between mb-4">
            <!-- Date Range Display -->
            <div class="mt-5 ml-5 font-bold text-md">
                <span>{{ formatDateRange }}</span>

                <!-- Custom Date Picker (Flatpickr)-->
                <div id="custom-date-picker" class="relative hidden lg:block">
                    <i class="fa-solid fa-chevron-down ml-2" data-toggle></i>
                    <input v-model="afterSpecificDate" type="hidden" data-input />
                </div>
            </div>

            <!-- Week Navigation Buttons -->
            <div class="flex gap-2">
                <ep-button variant="tertiary" @click="navigateWeek('prev')">
                    <i class="fa-solid fa-chevron-left text-xs"></i>
                </ep-button>
                <ep-button variant="tertiary" @click="navigateWeek('next')">
                    <i class="fa-solid fa-chevron-right text-xs"></i>
                </ep-button>
            </div>
        </div>

        <!-- Time Slots Table -->
        <div
            :style="{
                overflowX: 'auto',
                overflowY: maxSlots > 10 ? 'auto' : 'hidden',
                maxHeight: maxSlots > 10 ? '400px' : 'none',
            }"
        >

            <nord-banner v-if="Object.values(slotsByDay).flat().length === 0 && products.length > 0" variant="warning">
                {{ trans('online_booking.quick_booking.no_available_times') }}
            </nord-banner>

            <nord-banner v-if="products.length === 0" variant="info">
                {{ trans('online_booking.quick_booking.select_service_disclaimer') }}
            </nord-banner>

            <table class="table text-center table-fixed w-auto">
                <thead class="sticky top-0 bg-white z-2">
                    <tr class="border-hidden">
                        <!-- Table Headers: Days of the Week -->
                        <th
                            v-for="day in days"
                            :key="day.code"
                            :style="[
                                { textAlign: 'center', width: '1%' },
                                isColumnEmpty(day.code) ? { color: 'lightgrey' } : {},
                            ]"
                        >
                            {{ day.label }}
                            <br />
                            <span>{{ formatDayMonthShort(dateByDay[day.code]) }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <!-- Table Rows: Time Slots for Each Day -->
                    <tr v-for="rowIndex in maxSlots" :key="rowIndex" style="border-style: hidden">
                        <td v-for="day in days" :key="day.code" class="text-center w-[1%]" :style="{ minWidth: '60px', maxWidth: '70px', overflow: 'hidden', whiteSpace: 'nowrap' }">
                            <template
                                v-if="
                                    preferredDays.includes(day.code) &&
                                    slotsByDay[day.code] &&
                                    slotsByDay[day.code][rowIndex - 1]
                                "
                            >
                                <!-- Available Time Slot -->
                                <ep-button
                                    variant="secondary"
                                    class="rounded px-2 py-1 w-full"
                                    @click="selectSlot(slotsByDay[day.code][rowIndex - 1])"
                                >
                                    {{ formatTime(slotsByDay[day.code][rowIndex - 1].date) }}
                                </ep-button>
                            </template>
                            <!-- Empty slot placeholder -->
                            <span v-else class="text-[lightgrey]">-</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { http } from '@/system/http';
import moment from 'moment';
import Flatpickr from 'flatpickr';

export default {
    name: 'SuggestedAppointments',

    emits: ['slotSelected'], // Event emitted when a user selects a slot

    props: {
        // List of preferred time periods (e.g., ["Morning", "Afternoon"])
        preferredTimes: {
            type: Array,
            default: () => [],
        },
        // List of preferred days (e.g., ["mon", "tue"])
        preferredDays: {
            type: Array,
            default: () => [],
        },
        // Calendar ID (optional, used for filtering slots)
        calendarid: {
            type: [Number, String],
            default: '',
        },
        // List of product IDs used to filter available slots
        products: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            // Days of the week (used for table headers and slot mapping)
            days: [
                { code: 'mon', label: trans('online_booking.quick_booking.mon') },
                { code: 'tue', label: trans('online_booking.quick_booking.tue') },
                { code: 'wed', label: trans('online_booking.quick_booking.wed') },
                { code: 'thu', label: trans('online_booking.quick_booking.thu') },
                { code: 'fri', label: trans('online_booking.quick_booking.fri') },
                { code: 'sat', label: trans('online_booking.quick_booking.sat') },
                { code: 'sun', label: trans('online_booking.quick_booking.sun') },
            ],

            // Stores available days fetched from the API
            daysAvailable: [],

            // Holds available time slots, grouped by date
            timesAvailable: {},

            // The starting date of the displayed week (defaults to today)
            currentFrom: new Date().toISOString().split('T')[0],

            // Stores the last selected 'from' date
            fromDate: null,

            // Stores the user-selected custom date from the date picker
            afterSpecificDate: null,
        };
    },
    computed: {
        // Maps each weekday to its corresponding date in "YYYY-MM-DD" format
        dateByDay() {
            const monday = moment(this.currentFrom, 'YYYY-MM-DD').startOf('isoWeek');
            return this.days.reduce((acc, day, index) => {
                acc[day.code] = monday.clone().add(index, 'days').format('YYYY-MM-DD');
                return acc;
            }, {});
        },

        // Groups available slots by day while filtering based on user preferences
        slotsByDay() {
            return this.days.reduce((acc, day) => {
                if (this.preferredDays.includes(day.code)) {
                    acc[day.code] = (this.timesAvailable[this.dateByDay[day.code]]?.data || []).filter(slot =>
                        this.isWithinPreferredTime(slot.date.substring(11, 16)),
                    );
                }
                return acc;
            }, {});
        },

        // Formats the displayed date range at the top of the component
        formatDateRange() {
            if (!this.days.length || !this.dateByDay[this.days[0].code]) return '';

            const firstDate = moment(this.dateByDay[this.days[0].code], 'YYYY-MM-DD');
            const lastDate = moment(this.dateByDay[this.days[this.days.length - 1].code], 'YYYY-MM-DD');

            if (firstDate.month() === lastDate.month()) {
                return `${firstDate.date()} – ${lastDate.date()} ${lastDate.format('MMM YYYY')}`;
            }

            return `${firstDate.date()} ${firstDate.format('MMM')} – ${lastDate.date()} ${lastDate.format('MMM YYYY')}`;
        },

        // Determines the number of table rows based on the highest slot count for any day
        maxSlots() {
            const allSlotArrays = Object.values(this.timesAvailable).map(entry => entry.data);
            return allSlotArrays.length ? Math.max(5, ...allSlotArrays.map(arr => arr.length)) : 5;
        },
    },

    watch: {
        // Re-fetch slots when product selection changes
        products: 'fetchWeeklySlots',

        // Re-fetch slots if the selected calendar changes
        calendarid: 'fetchWeeklySlots',

        // When the user selects a custom date, update the slots accordingly
        afterSpecificDate(newDate) {
            if (newDate) {
                this.fromDate = this.getWeekForSpecificDate(); // Update fromDate
                this.fetchWeeklySlots();
            }
        },
    },

    mounted() {
        this.fetchWeeklySlots();
        this.initCustomDatePicker();
        this.formatDayMonthShort(new Date().toISOString().split('T')[0]);
    },

    methods: {
        // Fetches available weekly slots from the server
        async fetchWeeklySlots(triggerType = null) {
            const fromDate = this.getNewFromDate(triggerType);

            // Only update stored fromDate when navigating weeks
            if (triggerType) {
                this.fromDate = fromDate;
                this.afterSpecificDate = null;
            }

            const endDate = this.offsetDate(fromDate, 6);

            if (!this.products.length) {
                this.currentFrom = this.fromDate;
                this.daysAvailable = [];
                this.timesAvailable = {};
                return;
            }

            try {
                const { data: responseSlots } = await http.get('quick-booking/available-weekly-times', {
                    params: { from: fromDate, to: endDate, product_ids: this.products, calendar_id: this.calendarid },
                });

                // Process and organize the fetched slots by date
                this.timesAvailable = responseSlots.reduce((acc, slot) => {
                    if (slot.available && slot.calendar_id) {
                        const justDate = slot.date.substring(0, 10);
                        acc[justDate] = acc[justDate] || { day_of_week: this.getDayOfWeek(justDate), data: [] };
                        acc[justDate].data.push(slot);
                    }
                    return acc;
                }, {});

                this.currentFrom = fromDate;
            } catch (error) {
                console.error('Error fetching weekly times:', error);
            }
        },

        // Moves the week forward or backward based on direction
        navigateWeek(direction) {
            this.fetchWeeklySlots(direction);
        },

        // Initializes the custom date picker (Flatpickr)
        initCustomDatePicker() {
            const options = {
                wrap: true,
                enableTime: false,
                static: true,
                dateFormat: 'Y-m-d',
                onChange: (_, dateStr) => {
                    this.afterSpecificDate = dateStr;
                    this.fetchWeeklySlots();
                },
                onReady: (_, __, instance) => {
                    $(instance.calendarContainer).addClass('custom-navigation-picker');
                    document.querySelector('#custom-date-picker input[data-input]').setAttribute('hidden', 'true');
                },
            };

            Flatpickr(document.getElementById('custom-date-picker'), options);
        },

        // Returns the day of the week (e.g., "Mon", "Tue") for a given date
        getDayOfWeek(dateString) {
            return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(dateString).getDay()];
        },

        // Extracts and formats time from a datetime string (HH:mm)
        formatTime(datetime) {
            return datetime ? datetime.substring(11, 16) : '';
        },

        // Returns the start of the current week (Monday's date)
        getCurrentWeekMonday() {
            return moment().startOf('isoWeek').format('YYYY-MM-DD');
        },

        // Returns the start date for the next week
        getNextWeekFromDate() {
            return this.offsetDate(this.currentFrom || this.getCurrentWeekMonday(), 7);
        },

        // Returns the start date for the previous week
        getPrevWeekFromDate() {
            return this.offsetDate(this.currentFrom || this.getCurrentWeekMonday(), -7);
        },

        // Returns the Monday of the week that contains the selected custom date
        getWeekForSpecificDate() {
            return moment(this.afterSpecificDate, 'YYYY-MM-DD').startOf('isoWeek').format('YYYY-MM-DD');
        },

        // Determines the correct 'from' date based on navigation or user selection
        getNewFromDate(triggerType = null) {
            if (triggerType === 'next') return this.getNextWeekFromDate();
            if (triggerType === 'prev') return this.getPrevWeekFromDate();
            if (this.afterSpecificDate) return this.getWeekForSpecificDate();
            return this.fromDate || this.getCurrentWeekMonday();
        },

        // Adjusts a given date by a specified number of days
        offsetDate(dateString, daysOffset) {
            return moment(dateString, 'YYYY-MM-DD').add(daysOffset, 'days').format('YYYY-MM-DD');
        },

        // Checks if a given time is within the user's preferred time range
        isWithinPreferredTime(time) {
            const TIME_RANGES = {
                morning: ['06:00', '12:00'],
                afternoon: ['12:00', '17:00'],
                evening: ['17:00', '23:59'],
            };
            return this.preferredTimes.some(pref => {
                const range = TIME_RANGES[pref];
                return range && time >= range[0] && time < range[1];
            });
        },

        // Converts "YYYY-MM-DD" to "DD.MM" format
        formatDayMonthShort(dateString) {
            if (!dateString) return '';
            const [year, month, day] = dateString.split('-');
            return `${day}.${month}`;
        },

        // Checks if a specific day's column is empty (i.e., no available slots)
        isColumnEmpty(dayCode) {
            return !this.slotsByDay[dayCode]?.length;
        },

        // Handles slot selection and emits event with details
        selectSlot({ date, calendar_id }) {
            const start = moment(date, 'YYYY-MM-DDTHH:mm:ss');
            this.$emit('slotSelected', { start, end: start.clone().add(1, 'hours'), calendarId: calendar_id });
        },
    },
};
</script>
